const cleanValue = (value) => {
  const normalized = String(value ?? '').trim();
  return normalized && normalized.toLowerCase() !== 'none' ? normalized : '';
};

const activeItems = (items) =>
  Array.isArray(items) ? items.filter((item) => item && !item.deleted && !item.isDeleted) : [];

// The backend distinguishes legacy records by absent array fields. Present
// arrays are authoritative, including empty arrays left after removing values.
const legacyContactValue = (items, value) =>
  Array.isArray(items) ? '' : value;

const itemValue = (item, aliases) => {
  if (!item) return '';
  for (const alias of aliases) {
    const value = cleanValue(item[alias]);
    if (value) return value;
  }
  return '';
};

const uniqueSecondaryValues = (items, primaryItem, valueResolver) => {
  const primaryValue = cleanValue(valueResolver(primaryItem)).toLowerCase();
  const seen = new Set(primaryValue ? [primaryValue] : []);

  return items.reduce((values, item) => {
    if (item === primaryItem) return values;
    const value = cleanValue(valueResolver(item));
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return values;
    seen.add(key);
    values.push(value);
    return values;
  }, []);
};

export const getPrimaryItem = (items) => {
  const active = activeItems(items);
  return active.find((item) => item.is_primary) || null;
};

export const mergeContactConsentState = (contact, consent) => {
  if (!contact) return contact;

  const nextContact = { ...contact };
  if (typeof consent?.communicationConsent === 'boolean') {
    nextContact.subscribed = consent.communicationConsent;
    nextContact.communicationConsent = consent.communicationConsent;
  }
  if (consent?.emailConsent !== undefined) {
    nextContact.emailConsent = consent.emailConsent;
  }
  if (consent?.smsConsent !== undefined) {
    nextContact.smsConsent = consent.smsConsent;
  }
  if (consent?.pocketflowUnsubscribed !== undefined) {
    nextContact.pocketflow_unsubscribed = consent.pocketflowUnsubscribed;
  }

  return nextContact;
};

export const formatContactAddress = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return cleanValue(address);

  const line1 = cleanValue(address.line1 || address.address1);
  const line2 = cleanValue(address.line2 || address.address2);
  const city = cleanValue(address.city);
  const region = cleanValue(address.region || address.state);
  const postal = cleanValue(address.postal || address.postalCode || address.zip);
  const country = cleanValue(address.country);
  const locality = [city, region].filter(Boolean).join(', ');
  const localityWithPostal = [locality, postal].filter(Boolean).join(' ');

  return [line1, line2, localityWithPostal, country].filter(Boolean).join(', ');
};

export function normalizeContactRecord(contact = {}) {
  const source = contact || {};
  const emails = activeItems(source.emails);
  const phones = activeItems(source.phones);
  const addresses = activeItems(source.addresses);
  const tags = [];
  const seenTags = new Set();
  if (Array.isArray(source.tags)) {
    source.tags.forEach((tag) => {
      if (typeof tag !== 'string') return;
      const normalizedTag = cleanValue(tag);
      const normalizedKey = normalizedTag.toLowerCase();
      if (!normalizedTag || seenTags.has(normalizedKey)) return;
      seenTags.add(normalizedKey);
      tags.push(normalizedTag);
    });
  }
  const primaryEmailItem = getPrimaryItem(emails);
  const primaryPhoneItem = getPrimaryItem(phones);
  const primaryAddressItem = getPrimaryItem(addresses);
  const firstName = cleanValue(source.firstName);
  const lastName = cleanValue(source.lastName);
  const primaryEmail =
    itemValue(primaryEmailItem, ['value', 'email']) ||
    legacyContactValue(source.emails, cleanValue(source.email));
  const primaryPhone =
    itemValue(primaryPhoneItem, ['value', 'phone']) ||
    legacyContactValue(source.phones, cleanValue(source.phone || source.phoneNumber));
  const primaryAddress =
    formatContactAddress(primaryAddressItem) ||
    legacyContactValue(source.addresses, formatContactAddress(source.address));
  const fullName =
    cleanValue(source.fullName) || cleanValue(source.name) ||
    [firstName, lastName].filter(Boolean).join(' ');
  const organizationName = cleanValue(source.organizationName || source.organization);
  // Display may use the first live channel; primary value fields deliberately
  // retain their stricter marked-primary contract for editors and requests.
  const labelEmail = itemValue(primaryEmailItem || emails[0], ['email', 'value']) ||
    legacyContactValue(source.emails, cleanValue(source.email));
  const labelPhone = itemValue(primaryPhoneItem || phones[0], ['value', 'phone']) ||
    legacyContactValue(source.phones, cleanValue(source.phone || source.phoneNumber));
  const digits = labelPhone.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  const isDomestic = !labelPhone.startsWith('+') || digits.startsWith('1');
  const formattedPhone = isDomestic && national.length === 10
    ? `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
    : labelPhone;
  const displayName = fullName || organizationName || labelEmail || formattedPhone ||
    'Unknown contact';

  return {
    ...source,
    id: source.id || source.contactID || null,
    firstName,
    lastName,
    fullName,
    displayName,
    organizationName,
    emails,
    phones,
    addresses,
    tags,
    primaryEmail,
    primaryPhone,
    primaryAddress,
    secondaryEmails: uniqueSecondaryValues(emails, primaryEmailItem, (item) =>
      itemValue(item, ['value', 'email']),
    ),
    secondaryPhones: uniqueSecondaryValues(phones, primaryPhoneItem, (item) =>
      itemValue(item, ['value', 'phone']),
    ),
    secondaryAddresses: uniqueSecondaryValues(
      addresses,
      primaryAddressItem,
      formatContactAddress,
    ),
    emailConsent: resolveChannelConsent(source, 'email'),
    smsConsent: resolveChannelConsent(source, 'sms'),
    communicationConsent: source.communicationConsent !== false,
    allowProfileEdit: source.allowProfileEdit !== false,
    showTaxDocuments: source.showTaxDocuments !== false,
    totalCollected: Number(
      source.totalTransactionValue ?? source.totalCollected ?? source.totalAmount ?? 0,
    ),
    createdAt:
      source.created_at ||
      source.dateCreated ||
      source.createdAt ||
      source.dateRegistered ||
      source.timestamp ||
      null,
  };
}
import { resolveChannelConsent } from './channelConsent';
